// frontend/src/pages/staff/StaffServiceEdit.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import StaffSidebarLayout from "./StaffSidebarLayout";
import axios from "axios";
import "./staffStyles.css";
import PriceTiersEditor from "../../components/PriceTiersEditor";

// Utility function to convert text to user-friendly Title Case
const toUserFriendly = (text) => {
  if (!text) return "";
  return text
    .replace(/[-_]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

function StaffServiceEdit() {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    durationMinutes: "",
    pricingType: "fixed",
    hourlyRate: "",
    maxHoursPerBooking: 8,
    priceTiers: [],
    category: "haircut",
    applicableTo: [],
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [existingImage, setExistingImage] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [staffProfessionalTypes, setStaffProfessionalTypes] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Determine role type from path
  const getRoleType = useCallback(() => {
    return "professional";
  }, []);

  const getBasePath = useCallback(() => {
    const roleType = getRoleType();
    return `/dashboard/${roleType}`;
  }, [getRoleType]);

  // Security: Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error. Please contact support.");
      return null;
    }
    return baseURL;
  }, []);

  // Fetch service details
  useEffect(() => {
    const fetchService = async () => {
      try {
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) return;

        const res = await axios.get(`${baseURL}/api/staff/services`, {
          headers,
        });
        const service = res.data.services?.find((s) => s.id === serviceId);

        if (!service) {
          setErrorMsg("Service not found");
          setTimeout(() => navigate(`${getBasePath()}/services`), 2000);
          return;
        }

        setFormData({
          name: service.name || "",
          description: service.description || "",
          price: service.price || "",
          durationMinutes: service.durationMinutes || "",
          pricingType: service.pricingType || "fixed",
          hourlyRate: service.hourlyRate || "",
          maxHoursPerBooking: service.maxHoursPerBooking || 8,
          priceTiers: Array.isArray(service.priceTiers)
            ? service.priceTiers.map((tier) => ({
                label: tier.label || "",
                price: tier.price ?? "",
                durationMinutes: tier.durationMinutes ?? "",
              }))
            : [],
          category: service.category || "haircut",
          applicableTo: service.applicableTo || [],
        });

        // Fetch staff profile for professional types
        const staffResponse = await axios.get(`${baseURL}/api/staff/me`, {
          headers,
        });
        const staff = staffResponse.data.staff;
        const types = staff?.professionalTypes || [];
        setStaffProfessionalTypes(types);

        // Fetch business info to get business types
        if (staff.businessId) {
          const businessResponse = await axios.get(
            `${baseURL}/api/business/${staff.businessId}`,
            { headers },
          );
          const businessTypes = businessResponse.data?.type || [];

          // Fetch service categories based on business types (e.g., ['hair-studio', 'salon'])
          if (businessTypes && businessTypes.length > 0) {
            try {
              const businessCategoriesParam = businessTypes.join(",");
              const categoriesResponse = await axios.get(
                `${baseURL}/api/platform-settings/service-categories?businessCategories=${businessCategoriesParam}`,
              );
              const fetchedCategories =
                categoriesResponse.data.categories || [];
              if (fetchedCategories.length > 0) {
                setServiceCategories(fetchedCategories);
              }
            } catch (catErr) {
              // Fallback to default categories
              setServiceCategories([
                { value: "haircut", label: "Haircut" },
                { value: "color", label: "Color" },
                { value: "styling", label: "Styling" },
                { value: "treatment", label: "Treatment" },
                { value: "spa", label: "Spa" },
                { value: "nails", label: "Nails" },
                { value: "makeup", label: "Makeup" },
                { value: "waxing", label: "Waxing" },
                { value: "other", label: "Other" },
              ]);
            }
          }
        }
        setLoadingCategories(false);

        if (service.serviceImage) {
          // Check if image URL is already absolute (Azure Blob) or relative (local)
          const isAbsoluteUrl =
            service.serviceImage.startsWith("http://") ||
            service.serviceImage.startsWith("https://");
          setExistingImage(
            isAbsoluteUrl
              ? service.serviceImage
              : `${baseURL}${service.serviceImage}`,
          );
        }
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem("ogestyle_token");
          navigate("/login");
        } else {
          setErrorMsg("Unable to load service. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [serviceId, getAuthHeaders, getApiBaseUrl, navigate, getBasePath]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleApplicableToToggle = (type) => {
    setFormData((prev) => {
      const isSelected = prev.applicableTo.includes(type);
      const newApplicableTo = isSelected
        ? prev.applicableTo.filter((t) => t !== type)
        : [...prev.applicableTo, type].slice(0, 3);
      return { ...prev, applicableTo: newApplicableTo };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!validTypes.includes(file.type)) {
      setErrorMsg("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setErrorMsg("Image file size must be less than 5MB");
      return;
    }

    setImageFile(file);
    setErrorMsg("");

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Image upload function - COMMENTED OUT PER USER REQUEST
  /*
  const uploadImage = async () => {
    if (!imageFile) return null;

    try {
      setUploadingImage(true);
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return null;

      const formData = new FormData();
      formData.append('serviceImage', imageFile);

      const uploadRes = await axios.post(
        `${baseURL}/api/staff/services/upload-image`,
        formData,
        { 
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return uploadRes.data.imageUrl;
    } catch (err) {
      throw new Error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };
  */

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    // Validation
    if (!formData.name.trim()) {
      setErrorMsg("Service name is required");
      return;
    }
    if (
      formData.pricingType !== "range" &&
      formData.pricingType !== "hourly" &&
      (!formData.price || formData.price <= 0)
    ) {
      setErrorMsg("Valid price is required");
      return;
    }
    if (
      formData.pricingType !== "hourly" &&
      formData.pricingType !== "range" &&
      (!formData.durationMinutes || formData.durationMinutes < 5)
    ) {
      setErrorMsg("Duration must be at least 5 minutes");
      return;
    }

    try {
      setSubmitting(true);

      // Upload new image if selected - COMMENTED OUT PER USER REQUEST
      /*
      let imageUrl = null;
      if (imageFile) {
        imageUrl = await uploadImage();
        if (!imageUrl) {
          setErrorMsg('Failed to upload image');
          return;
        }
      }
      */

      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      // Prepare update data
      const updateData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        durationMinutes: parseInt(formData.durationMinutes),
        category: formData.category,
        applicableTo: formData.applicableTo,
        pricingType: formData.pricingType || "fixed",
        // F1: per-unit - base price is the per-unit price, duration is per unit
        ...(formData.pricingType === "per_unit"
          ? {
              pricePerUnit: parseFloat(formData.price),
              maxUnitsPerBooking: 10,
            }
          : {}),
      };

      // F2: hourly = rate x hours; range = customer picks one of the options
      if (formData.pricingType === "hourly") {
        const rate = parseFloat(formData.hourlyRate);
        if (Number.isFinite(rate)) {
          updateData.hourlyRate = rate;
          updateData.price = rate;
        }
        updateData.maxHoursPerBooking = parseInt(
          formData.maxHoursPerBooking || 8,
          10,
        );
        // Duration field is hidden for hourly - the engine bills 60 min per hour.
        updateData.durationMinutes = 60;
      }
      if (formData.pricingType === "range") {
        const usableTiers = (formData.priceTiers || [])
          .map((tier) => ({
            label: String(tier.label || "").trim(),
            price: parseFloat(tier.price),
            durationMinutes: parseInt(tier.durationMinutes, 10),
          }))
          .filter((tier) => tier.label && Number.isFinite(tier.price));

        if (usableTiers.length < 2) {
          setErrorMsg(
            "Add at least 2 priced options (for example: Shoulder length $150, Mid-back $220).",
          );
          return;
        }

        const missingMinutes = usableTiers.some(
          (tier) =>
            !Number.isFinite(tier.durationMinutes) || tier.durationMinutes < 5,
        );
        if (missingMinutes) {
          setErrorMsg("Give every option a duration of at least 5 minutes.");
          return;
        }

        const tierPrices = usableTiers.map((t) => t.price);
        updateData.priceTiers = usableTiers;
        updateData.price = Math.min(...tierPrices);
        updateData.maxPrice = Math.max(...tierPrices);
        // Duration field is hidden for range - use the longest option length.
        updateData.durationMinutes = Math.max(
          ...usableTiers.map((t) => Number(t.durationMinutes) || 0),
        );
      }

      // Only include image if a new one was uploaded - COMMENTED OUT
      /*
      if (imageUrl) {
        updateData.serviceImage = imageUrl;
      }
      */

      await axios.patch(
        `${baseURL}/api/staff/services/${serviceId}`,
        updateData,
        { headers },
      );

      setSuccessMsg("Service updated successfully!");
      setTimeout(() => {
        navigate(`${getBasePath()}/services`);
      }, 1500);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setErrorMsg(
          err?.response?.data?.message ||
            "Failed to update service. Please try again.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const roleType = getRoleType();
  const basePath = getBasePath();

  if (loading) {
    return (
      <StaffSidebarLayout roleType={roleType}>
        <div className="staff-profile">
          <p>Loading service...</p>
        </div>
      </StaffSidebarLayout>
    );
  }

  return (
    <StaffSidebarLayout roleType={roleType}>
      <div className="staff-profile">
        <button
          onClick={() => navigate(`${basePath}/services`)}
          style={{
            background: "none",
            border: "none",
            color: "#001d4a",
            fontSize: "16px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 0",
            marginBottom: "24px",
            transition: "color 0.2s ease",
          }}
          onMouseOver={(e) => (e.target.style.color = "#002d6e")}
          onMouseOut={(e) => (e.target.style.color = "#001d4a")}
        >
          <span style={{ fontSize: "20px" }}>←</span> Back to Services
        </button>

        <h2>Edit Service</h2>

        {errorMsg && <div className="alert-error">{errorMsg}</div>}

        {successMsg && <div className="alert-success">{successMsg}</div>}

        <div className="profile-info" style={{ padding: "32px" }}>
          <form onSubmit={handleSubmit}>
            {/* Service Name */}
            <div className="form-group">
              <label>Service Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Bob Cut, Cornrows, Facial"
                required
              />
            </div>

            {/* Description */}
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Brief description of the service"
                rows={4}
              />
            </div>

            {/* Category */}
            <div className="form-group">
              <label>Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                {loadingCategories ? (
                  <option disabled>Loading categories...</option>
                ) : (
                  serviceCategories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Pricing Type - Fixed + Per Unit + Hourly + Options */}
            <div className="form-group">
              <label>Pricing Type</label>
              <select
                name="pricingType"
                value={formData.pricingType}
                onChange={handleChange}
              >
                <option value="fixed">Fixed Price</option>
                <option value="per_unit">Per Unit (e.g. per nail)</option>
                <option value="hourly">Hourly (rate x hours)</option>
                <option value="range">Options (customer picks one)</option>
                {/* From / Variable hidden until properly wired */}
              </select>
              <p style={{ fontSize: "12px", color: "#666", marginTop: "6px" }}>
                {formData.pricingType === "fixed" &&
                  "Exact price for the service"}
                {formData.pricingType === "hourly" &&
                  "Charged per hour of work"}
                {formData.pricingType === "range" &&
                  "Customers pick one priced option"}
                {formData.pricingType === "per_unit" &&
                  "Per unit pricing: Price above = price per unit; Duration = time per unit (e.g. 15 min per nail). Customers pick quantity (max 10)."}
              </p>
            </div>

            {/* Hourly Rate - Only for hourly pricing */}
            {formData.pricingType === "hourly" && (
              <div className="form-group">
                <label>Hourly Rate ($) *</label>
                <input
                  type="number"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleChange}
                  placeholder="Rate per hour"
                  step="0.01"
                  min="0"
                  required
                />
                <p style={{ fontSize: "12px", color: "#666", marginTop: "6px" }}>
                  The appointment length is the number of hours the customer
                  books (hours x 60 minutes).
                </p>
                <label style={{ marginTop: "12px", display: "block" }}>
                  Max hours per booking
                </label>
                <input
                  type="number"
                  name="maxHoursPerBooking"
                  value={formData.maxHoursPerBooking}
                  onChange={handleChange}
                  min="1"
                  max="24"
                />
                <p style={{ fontSize: "12px", color: "#666", marginTop: "6px" }}>
                  Customers pick how many hours they need. Total price = hourly
                  rate x hours, and the appointment length scales the same way.
                </p>
              </div>
            )}

            {/* Price options - Only for range pricing (customer picks one) */}
            {formData.pricingType === "range" && (
              <div className="form-group">
                <label>Price options</label>
                <PriceTiersEditor
                  tiers={formData.priceTiers || []}
                  onChange={(nextTiers) =>
                    setFormData((prev) => ({ ...prev, priceTiers: nextTiers }))
                  }
                />
              </div>
            )}

            {/* Price and Duration */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
              }}
            >
              {formData.pricingType !== "range" &&
                formData.pricingType !== "hourly" && (
              <div className="form-group">
                <label>
                  {formData.pricingType === "per_unit"
                    ? "Price per unit ($)"
                    : "Price ($)"}{" "}
                  *
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  required
                />
              </div>
              )}

              {formData.pricingType !== "hourly" &&
                formData.pricingType !== "range" && (
              <div className="form-group">
                <label>
                  {formData.pricingType === "per_unit"
                    ? "Minutes per unit"
                    : "Duration (minutes)"}{" "}
                  *
                </label>
                <input
                  type="number"
                  name="durationMinutes"
                  value={formData.durationMinutes}
                  onChange={handleChange}
                  placeholder="30"
                  min="5"
                  step="5"
                  required
                />
              </div>
              )}
            </div>

            {/* Applicable To (Professional Types) */}
            {staffProfessionalTypes.length > 1 && (
              <div className="form-group">
                <label>Service Applies To</label>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    marginBottom: "10px",
                  }}
                >
                  Leave empty if this service applies to all your professional
                  types. Otherwise, select which types (max 3).
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {staffProfessionalTypes.map((type) => (
                    <label
                      key={type}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "10px 12px",
                        border: "2px solid",
                        borderColor: formData.applicableTo.includes(type)
                          ? "#001d4a"
                          : "#ddd",
                        borderRadius: "8px",
                        cursor: "pointer",
                        background: formData.applicableTo.includes(type)
                          ? "#e8f4f8"
                          : "white",
                        transition: "all 0.2s",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.applicableTo.includes(type)}
                        onChange={() => handleApplicableToToggle(type)}
                        disabled={
                          formData.applicableTo.length >= 3 &&
                          !formData.applicableTo.includes(type)
                        }
                        style={{ marginRight: "8px" }}
                      />
                      <span style={{ fontSize: "14px", fontWeight: "500" }}>
                        {toUserFriendly(type)}
                      </span>
                    </label>
                  ))}
                </div>
                <p
                  style={{
                    margin: "5px 0 0 0",
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  Selected {formData.applicableTo.length} type
                  {formData.applicableTo.length !== 1 ? "s" : ""}
                </p>
              </div>
            )}

            {/* Service Image - COMMENTED OUT PER USER REQUEST
          <div className="form-group">
            <label>Service Image</label>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleImageChange}
            />
            <small style={{ color: '#666', display: 'block', marginTop: '8px' }}>
              Upload a new image to replace the existing one (max 5MB, JPEG/PNG/GIF/WebP)
            </small>
          </div>

          {/* Image Preview */}
            {/* {(imagePreview || existingImage) && (
            <div style={{ marginTop: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {imagePreview ? 'New Image Preview' : 'Current Image'}
              </label>
              <img
                src={imagePreview || existingImage}
                alt="Service preview"
                style={{
                  width: '200px',
                  height: '200px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '1px solid #ddd'
                }}
              />
            </div>
          )} */}
            {/* END Service Image */}

            {/* Submit Button */}
            <div style={{ marginTop: "30px", display: "flex", gap: "15px" }}>
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting || uploadingImage}
                style={{ flex: 1 }}
              >
                {uploadingImage
                  ? "Uploading Image..."
                  : submitting
                    ? "Updating..."
                    : "Update Service"}
              </button>
              <button
                type="button"
                onClick={() => navigate(`${basePath}/services`)}
                className="btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </StaffSidebarLayout>
  );
}

export default StaffServiceEdit;
